import { useEffect, useState } from 'react';

/**
 * LiveAppsStrip — social proof for the landing page.
 *
 * Pulls the live count of deployed agents + their names from the public
 * analytics endpoint (GET /analytics/public/agents, no auth) and renders
 * "N live apps — each with its own AI agent" above a gently scrolling row of
 * real app names.
 *
 * Deliberately narrow: it shows the ONE flattering, on-message number (apps
 * deployed) and never the small invocation/turn counts. Fails silent — if the
 * endpoint is down or returns nothing, the component renders null so the hero
 * is never left with a broken or empty proof block.
 */

const BASE_URL = (import.meta.env.VITE_FLOWSTACK_BASE_URL as string) || 'https://sage-api.flowstack.fun';

interface Agent { name: string; url: string | null; }
interface NetworkData { totals: { agents: number }; agents: Agent[]; }

export default function LiveAppsStrip() {
  const [data, setData] = useState<NetworkData | null>(null);

  useEffect(() => {
    let active = true;
    fetch(`${BASE_URL}/analytics/public/agents`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => { if (active) setData(d); })
      .catch(() => { /* fail silent — render nothing */ });
    return () => { active = false; };
  }, []);

  const count = data?.totals?.agents ?? 0;

  // Dedupe names (several apps share a name) and keep it to a tidy row.
  const names = Array.from(
    new Set((data?.agents ?? []).map((a) => a.name?.trim()).filter(Boolean) as string[])
  ).slice(0, 16);

  // Nothing real to show → don't render a dead block on the hero.
  if (count < 1 || names.length < 4) return null;

  // Duplicate the list so the marquee loops seamlessly.
  const row = [...names, ...names];

  return (
    <section className="w-full max-w-3xl mx-auto mt-12" aria-label="Live apps on the network">
      <style>{`@keyframes fs-marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }`}</style>

      <p className="text-center text-sm text-[var(--color-text-secondary)] mb-4">
        <span className="font-semibold text-[var(--color-accent)]">{count.toLocaleString('en-US')} live apps</span>
        {' — each shipped with its own AI agent.'}
      </p>

      <div
        className="relative overflow-hidden"
        style={{ maskImage: 'linear-gradient(90deg, transparent, #000 12%, #000 88%, transparent)', WebkitMaskImage: 'linear-gradient(90deg, transparent, #000 12%, #000 88%, transparent)' }}
      >
        <div className="flex w-max gap-2.5" style={{ animation: 'fs-marquee 40s linear infinite' }}>
          {row.map((name, i) => (
            <span
              key={`${name}-${i}`}
              className="whitespace-nowrap px-3.5 py-1.5 rounded-full border border-[var(--color-border)] bg-[var(--color-surface)] text-xs text-[var(--color-text-secondary)]"
            >
              {name}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
